/**
 * garage-core.js — Factory 47 shared garage / fitment helper.
 *
 * Single source of truth for reading the EasySearch YMM garage vehicle and
 * evaluating fitment from esr-tags + the baked f47-fitment-map.json. Used by
 * both garage-fit.js (collection fit badges) and garage-recommendations.js
 * ("Complete your build"). Do NOT create a parallel vehicle state — read this.
 *
 * Exposes window.F47Garage:
 *   readActiveVehicle() -> { year, modelKey, label } | null
 *   loadFitmentMap()    -> Promise<map|null>   (memoised)
 *   resolveSlugs(vehicle, map) -> Set<slugString> | null
 *   fits(vehicleSlugSet, itemSlugArray, isUniversal) -> bool
 *   safe(fn, fallback)
 *
 * Map URL comes from this script tag's data-fitment-map attribute.
 */
window.F47Garage = (function () {
  'use strict';

  var STORAGE_KEY = 'easysearch-garage-items';
  var DELIM = 'easysearch-preselect-delimiter';

  var scriptEl = document.getElementById('garage-core-script') || document.currentScript;
  var MAP_URL = scriptEl ? scriptEl.getAttribute('data-fitment-map') : null;
  var mapPromise = null;

  function safe(fn, fallback) {
    try { return fn(); } catch (e) { return fallback; }
  }

  // First garage entry is the active vehicle (My-Garage multi-select index is app-internal).
  function readActiveVehicle() {
    return safe(function () {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      var list = obj && obj.value;
      if (!Array.isArray(list) || !list.length) return null;
      var parts = String(list[0]).split(DELIM);
      var year = parseInt((parts[0] || '').trim(), 10);
      var modelRaw = (parts[1] || '').trim();
      if (!year || !modelRaw) return null;
      return { year: year, modelKey: modelRaw.replace(/\s+/g, ' ').toLowerCase(), label: year + ' ' + modelRaw };
    }, null);
  }

  function loadFitmentMap() {
    if (mapPromise) return mapPromise;
    if (!MAP_URL || !window.fetch) { mapPromise = Promise.resolve(null); return mapPromise; }
    mapPromise = fetch(MAP_URL, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return mapPromise;
  }

  // Vehicle -> Set of collection-slug strings it maps to. null if unknown vehicle.
  function resolveSlugs(vehicle, map) {
    if (!vehicle || !map || !map.models) return null;
    var entries = map.models[vehicle.modelKey];
    if (!entries || !entries.length) return null;
    var slugs = new Set();
    for (var i = 0; i < entries.length; i++) {
      var lo = entries[i][0], hi = entries[i][1], slug = entries[i][2];
      if (vehicle.year >= lo && vehicle.year <= hi) slugs.add(slug);
    }
    return slugs;
  }

  function fits(vehicleSlugSet, itemSlugArray, isUniversal) {
    if (isUniversal) return true;
    if (!vehicleSlugSet || !itemSlugArray || !itemSlugArray.length) return false;
    for (var i = 0; i < itemSlugArray.length; i++) { if (vehicleSlugSet.has(itemSlugArray[i])) return true; }
    return false;
  }

  return {
    STORAGE_KEY: STORAGE_KEY,
    readActiveVehicle: readActiveVehicle,
    loadFitmentMap: loadFitmentMap,
    resolveSlugs: resolveSlugs,
    fits: fits,
    safe: safe
  };
})();
